import { ApiOutlined } from '@ant-design/icons';
import { Alert, Col, Form, Row, Select, Switch, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import { TiendaPerfectaCompany } from 'models/TiendaPerfecta';

import { FunctionsHelper } from 'utils/FunctionsHelper';

const I18N_BASE =
  'scenes.settings.companies.components.tienda_perfecta_integration_fields';

/** Nombre del campo en el Form, compartido por crear y editar. */
export const TIENDA_PERFECTA_COMPANY_FIELD = 'tiendaPerfectaCompanyCode';

interface TiendaPerfectaIntegrationFieldsProps {
  enabled: boolean;
  onEnabledChange: (checked: boolean) => void;
  companies: TiendaPerfectaCompany[];
  companiesLoading?: boolean;
  disabled?: boolean;
  /** Code guardado que ya no existe en el catálogo. Ver `orphanCompanyCode`. */
  orphanCompanyCode?: string | null;
  /** Code elegido en el formulario, para el resumen del header. */
  selectedCompanyCode?: string | null;
}

function TiendaPerfectaIntegrationFields({
  enabled,
  onEnabledChange,
  companies,
  companiesLoading = false,
  disabled = false,
  orphanCompanyCode = null,
  selectedCompanyCode = null,
}: TiendaPerfectaIntegrationFieldsProps) {
  const { t } = useTranslation();

  const selectedCompany = companies.find((c) => c.code === selectedCompanyCode);

  const summary = !enabled ? (
    <Tag>{t(`${I18N_BASE}.summary.disabled`)}</Tag>
  ) : selectedCompany ? (
    <Tag color="blue">{selectedCompany.name}</Tag>
  ) : (
    <Tag color="red">{t(`${I18N_BASE}.summary.missing_company`)}</Tag>
  );

  return (
    <IntegrationCard
      title={t(`${I18N_BASE}.title`)}
      icon={<ApiOutlined />}
      summary={summary}
    >
      <Row gutter={24}>
        <Col xs={24}>
          <Form.Item style={{ marginBottom: enabled ? 16 : 0 }}>
            <Switch
              checked={enabled}
              onChange={onEnabledChange}
              disabled={disabled}
            />
            <span style={{ marginLeft: 10 }}>
              {t(`${I18N_BASE}.labels.enable`)}
            </span>
          </Form.Item>
        </Col>
      </Row>

      {enabled && (
        <Row gutter={24}>
          <Col xs={24}>
            <Form.Item
              name={TIENDA_PERFECTA_COMPANY_FIELD}
              label={t(`${I18N_BASE}.labels.company`)}
              required
              style={{ marginBottom: orphanCompanyCode ? 16 : 0 }}
              rules={[
                {
                  required: true,
                  message: t(`${I18N_BASE}.validations.company.required`),
                },
              ]}
            >
              <Select
                options={companies.map((c) => ({
                  value: c.code,
                  label: c.name,
                  title: `${c.name} ${c.code}`,
                }))}
                showSearch
                allowClear
                disabled={disabled || companiesLoading}
                loading={companiesLoading}
                filterOption={(search: string, option?: any) =>
                  FunctionsHelper.normalizeText(option?.title ?? '').includes(
                    FunctionsHelper.normalizeText(search)
                  )
                }
                placeholder={t(`${I18N_BASE}.placeholders.company`)}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {enabled && orphanCompanyCode && (
        <Alert
          type="warning"
          showIcon
          message={t(`${I18N_BASE}.alerts.orphan_company`, {
            code: orphanCompanyCode,
          })}
        />
      )}
    </IntegrationCard>
  );
}

export default TiendaPerfectaIntegrationFields;
